/** Staff-facing wording for the "Waiting for Player" step (Shop portal + Cashier). */
export const REDEEM_CONSENT_COPY = {
    sendForApproval: 'Send to Player for approval',
    sending: 'Sending…',
    waitingTitle: (playerName) => `Waiting for ${playerName} to approve`,
    appHint: 'The Player can also tap Approve in the Player app — this screen updates by itself.',
    codeLabel: 'Code from the Player',
    codePlaceholder: '6-digit code',
    confirm: 'Confirm redeem',
    confirming: 'Confirming…',
    resend: 'Resend code',
    resendIn: (seconds) => `Resend in ${seconds}s`,
    resendsUsed: 'No resends left',
    cancel: 'Cancel request',
    startAgain: 'Start again',
    close: 'Close',
    done: 'Done',
    expiresIn: (clock) => `Expires in ${clock}`,
    triesLeft: (n) => (n === 1 ? '1 try left' : `${n} tries left`),
    noContact: 'This Player has no verified phone or email, so no code was sent. Ask them to open the Player app and tap Approve.',
    limitTitle: (playerName, count, limit) => limit <= 1 ? `${playerName} already has a redeem waiting` : `${playerName} has ${count} redeems waiting (limit ${limit})`,
    limitBody: (limit) => limit <= 1
        ? 'Only one redeem can wait for a Player’s approval at a time. Resume it, cancel it, or wait until the Player answers or it expires.'
        : `Only ${limit} redeems can wait for a Player’s approval at a time. Resume or cancel one, or wait until one is answered or expires.`,
    resume: 'Resume',
    cancelAndSend: 'Cancel it and send this one',
    cancelling: 'Cancelling…',
    sendNew: 'Send new request',
    backToForm: 'Back to form',
    expiredNow: 'Expired — you can send a new request.',
    cannotCancel: 'Only the sender or the Shop portal can cancel it.',
};
/** "Sent by you" / "Sent by Cashier Amy" / "Sent from the Shop portal". */
export function pendingSenderLabel(summary) {
    if (summary.mine)
        return 'Sent by you';
    if (summary.initiator_type === 'cashier')
        return summary.initiator_name ? `Sent by Cashier ${summary.initiator_name}` : 'Sent by a Cashier';
    return 'Sent from the Shop portal';
}
/** The limit details from a REDEEM_REQUEST_LIMIT_REACHED refusal, or null for any other result. */
export function pendingLimitFrom(result) {
    if (result.ok || result.code !== 'REDEEM_REQUEST_LIMIT_REACHED')
        return null;
    const data = (result.data || {});
    const pending = Array.isArray(data.pending) ? data.pending : [];
    return { limit: Number(data.limit) > 0 ? Number(data.limit) : 1, pending };
}
export function secondsUntil(iso, now = Date.now()) {
    if (!iso)
        return 0;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t))
        return 0;
    return Math.max(0, Math.ceil((t - now) / 1000));
}
export function formatCountdown(totalSeconds) {
    const s = Math.max(0, Math.floor(totalSeconds));
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, '0')}`;
}
/** Where the code went, or what to do when it could not be sent. */
export function deliveryMessage(request) {
    const { channel, destination_masked: to, status, error } = request.delivery;
    if (!channel)
        return REDEEM_CONSENT_COPY.noContact;
    const via = channel === 'sms' ? 'SMS' : 'email';
    if (status === 'failed') {
        return `The code could not be sent by ${via}${error ? ` (${error})` : ''}. Resend it, or ask the Player to approve in the app.`;
    }
    return `Code sent by ${via}${to ? ` to ${to}` : ''}. Ask the Player for the code.`;
}
/** Final-state banner; null while the request is still pending. */
export function outcomeMessage(request) {
    var _a;
    switch (request.status) {
        case 'approved':
            return {
                title: request.decided_via === 'app' ? 'Approved in the Player app' : 'Approved with the Player’s code',
                body: 'The redeem is recorded.',
                tone: 'success',
            };
        case 'declined':
            return { title: 'The Player declined', body: 'No credits were moved.', tone: 'danger' };
        case 'expired':
            return { title: 'Request expired', body: 'The Player did not approve in time. No credits were moved.', tone: 'neutral' };
        case 'cancelled':
            return {
                title: request.cancel_reason === 'superseded' ? 'Replaced by a newer request' : 'Request cancelled',
                body: 'No credits were moved.',
                tone: 'neutral',
            };
        case 'voided':
            return { title: 'Too many wrong codes', body: 'This request was stopped. No credits were moved.', tone: 'danger' };
        case 'failed':
            return {
                title: 'The redeem could not be completed',
                body: `${((_a = request.failure) === null || _a === void 0 ? void 0 : _a.message) || 'Something went wrong.'} No credits were moved.`,
                tone: 'danger',
            };
        default:
            return null;
    }
}
export function canResend(request, now = Date.now()) {
    return (request.status === 'pending' &&
        !!request.delivery.channel &&
        request.resend.count < request.resend.max &&
        secondsUntil(request.resend.next_resend_at, now) === 0);
}
export function onlyDigits6(value) {
    return String(value || '').replace(/\D/g, '').slice(0, 6);
}
